/* eslint-disable react/no-multi-comp */

import { isString, extend, each, has, map, includes, findIndex, find, fromPairs, clone, isEmpty } from "lodash";
import React, { Fragment, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Select from "antd/lib/select";
import Table from "antd/lib/table";
import Popover from "antd/lib/popover";
import Button from "antd/lib/button";
import Tag from "antd/lib/tag";
import Input from "antd/lib/input";
import Radio from "antd/lib/radio";
import Form from "antd/lib/form";
import Tooltip from "@/components/Tooltip";
import ParameterValueInput from "@/components/parameters/ParameterValueInput";
import { ParameterMappingType } from "@/services/widget";
import { Parameter, cloneParameter } from "@/services/parameters";
import HelpTrigger from "@/components/help-trigger/HelpTrigger";
import { useTheme } from "@/components/ThemeProvider";
import QuestionCircleFilledIcon from "@ant-design/icons/QuestionCircleFilled";
import EditOutlinedIcon from "@ant-design/icons/EditOutlined";
import CloseOutlinedIcon from "@ant-design/icons/CloseOutlined";
import CheckOutlinedIcon from "@ant-design/icons/CheckOutlined";

import "./ParameterMappingInput.less";

export const MappingType = {
  DashboardAddNew: "dashboard-add-new",
  DashboardMapToExisting: "dashboard-map-to-existing",
  WidgetLevel: "widget-level",
  StaticValue: "static-value",
};


function TitleEditor({ existingParams, mapping, onChange }) {
  const [showPopup, setShowPopup] = useState(false);
  const [title, setTitle] = useState("");

  const onPopupVisibleChange = useCallback((visible) => {
    setShowPopup(visible);
    if (visible) {
      setTitle(getMappingTitle());
    }
  }, [getMappingTitle]);

  const onEditingTitleChange = useCallback((event) => {
    setTitle(event.target.value);
  }, []);

  const getMappingTitle = useCallback(() => {
    let currentMapping = mapping;

    if (isString(currentMapping.title) && currentMapping.title !== "") {
      return currentMapping.title;
    }

    // if mapped to dashboard, find source param and return its title
    if (currentMapping.type === MappingType.DashboardMapToExisting) {
      const source = find(existingParams, { name: currentMapping.mapTo });
      if (source) {
        currentMapping = source;
      }
    }

    return currentMapping.title || currentMapping.param.title;
  }, [mapping, existingParams]);

  const save = useCallback(() => {
    const newMapping = extend({}, mapping, { title });
    onChange(newMapping);
    setShowPopup(false);
  }, [mapping, title, onChange]);

  const hide = useCallback(() => {
    setShowPopup(false);
  }, []);

  const renderPopover = () => {
    const { param: { title: paramTitle } } = mapping;

    return (
      <div className="parameter-mapping-title-editor">
        <Input
          size="small"
          value={title}
          placeholder={paramTitle}
          aria-label="Edit parameter title"
          onChange={onEditingTitleChange}
          onPressEnter={save}
          maxLength={100}
          autoFocus
        />
        <Button size="small" type="dashed" onClick={hide}>
          <CloseOutlinedIcon />
        </Button>
        <Button size="small" type="dashed" onClick={save}>
          <CheckOutlinedIcon />
        </Button>
      </div>
    );
  };

  const renderEditButton = () => {
    if (mapping.type === MappingType.StaticValue) {
      return (
        <Tooltip placement="right" title="Titles for static values don't appear in widgets">
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
          <span tabIndex={0}>
            <i className="fa fa-eye-slash" aria-hidden="true" />
          </span>
        </Tooltip>
      );
    }
    return (
      <Popover
        placement="right"
        trigger="click"
        content={renderPopover()}
        visible={showPopup}
        onVisibleChange={onPopupVisibleChange}>
        <Button size="small" type="dashed">
          <EditOutlinedIcon />
        </Button>
      </Popover>
    );
  };

  const disabled = mapping.type === MappingType.StaticValue;

  return (
    <div className={classNames("parameter-mapping-title", { disabled })}>
      <span className="text">{getMappingTitle()}</span>
      {renderEditButton()}
    </div>
  );
}


TitleEditor.propTypes = {
  existingParams: PropTypes.arrayOf(PropTypes.object),
  mapping: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  onChange: PropTypes.func.isRequired,
};

TitleEditor.defaultProps = {
  existingParams: [],
};

export function parameterMappingsToEditableMappings(mappings, parameters, existingParameterNames = []) {
  return map(mappings, mapping => {
    const result = extend({}, mapping);
    const alreadyExists = includes(existingParameterNames, mapping.mapTo);
    result.param = find(parameters, p => p.name === mapping.name);
    switch (mapping.type) {
      case ParameterMappingType.DashboardLevel:
        result.type = alreadyExists ? MappingType.DashboardMapToExisting : MappingType.DashboardAddNew;
        result.value = null;
        break;
      case ParameterMappingType.StaticValue:
        result.type = MappingType.StaticValue;
        result.param = cloneParameter(result.param);
        result.param.setValue(result.value);
        break;
      case ParameterMappingType.WidgetLevel:
        result.type = MappingType.WidgetLevel;
        result.value = null;
        break;
      // no default
    }
    return result;
  });
}

export function editableMappingsToParameterMappings(mappings) {
  return fromPairs(
    map(
      // convert to map
      mappings,
      mapping => {
        const result = extend({}, mapping);
        switch (mapping.type) {
          case MappingType.DashboardAddNew:
            result.type = ParameterMappingType.DashboardLevel;
            result.value = null;
            break;
          case MappingType.DashboardMapToExisting:
            result.type = ParameterMappingType.DashboardLevel;
            result.value = null;
            break;
          case MappingType.StaticValue:
            result.type = ParameterMappingType.StaticValue;
            result.param = cloneParameter(mapping.param);
            result.param.setValue(result.value);
            result.value = result.param.value;
            break;
          case MappingType.WidgetLevel:
            result.type = ParameterMappingType.WidgetLevel;
            result.value = null;
            break;
          // no default
        }
        delete result.param;
        return [result.name, result];
      }
    )
  );
}

export function synchronizeWidgetTitles(sourceMappings, widgets) {
  const affectedWidgets = [];

  each(sourceMappings, sourceMapping => {
    if (sourceMapping.type === ParameterMappingType.DashboardLevel) {
      each(widgets, widget => {
        const widgetMappings = widget.options.parameterMappings;
        each(widgetMappings, widgetMapping => {
          // check if mapped to the same dashboard-level parameter
          if (
            widgetMapping.type === ParameterMappingType.DashboardLevel &&
            widgetMapping.mapTo === sourceMapping.mapTo
          ) {
            // dirty check - update only when needed
            if (widgetMapping.title !== sourceMapping.title) {
              widgetMapping.title = sourceMapping.title;
              affectedWidgets.push(widget);
            }
          }
        });
      });
    }
  });

  return affectedWidgets;
}

export function ParameterMappingInput({ mapping, existingParamNames, onChange, inputError }) {
  const { isDarkMode } = useTheme();
  const [localMapping, setLocalMapping] = useState(mapping);

  useEffect(() => {
    setLocalMapping(mapping);
  }, [mapping]);

  const formItemProps = {
    labelCol: { span: 5 },
    wrapperCol: { span: 16 },
    className: "form-item",
  };

  const updateSourceType = useCallback((type) => {
    let mapTo = localMapping.mapTo;

    // if mapped name doesn't already exists
    // default to first select option
    if (type === MappingType.DashboardMapToExisting && !includes(existingParamNames, mapTo)) {
      mapTo = existingParamNames[0];
    }

    updateParamMapping({ type, mapTo });
  }, [localMapping, existingParamNames, updateParamMapping]);

  const updateParamMapping = useCallback((update) => {
    const newMapping = extend({}, localMapping, update);
    if (newMapping.value !== localMapping.value) {
      newMapping.param = cloneParameter(newMapping.param);
      newMapping.param.setValue(newMapping.value);
    }
    if (has(update, "type")) {
      if (update.type === MappingType.StaticValue) {
        newMapping.value = newMapping.param.value;
      } else {
        newMapping.value = null;
      }
    }
    setLocalMapping(newMapping);
    onChange(newMapping);
  }, [localMapping, onChange]);

  const noExisting = existingParamNames.length === 0;

  const renderMappingTypeSelector = () => (
    <Radio.Group value={localMapping.type} onChange={e => updateSourceType(e.target.value)}>
      <Radio className="radio" value={MappingType.DashboardAddNew} data-test="NewDashboardParameterOption">
        New dashboard parameter
      </Radio>
      <Radio className="radio" value={MappingType.DashboardMapToExisting} disabled={noExisting}>
        Existing dashboard parameter{" "}
        {noExisting ? (
          <Tooltip title="There are no dashboard parameters corresponding to this data type">
            <QuestionCircleFilledIcon />
          </Tooltip>
        ) : null}
      </Radio>
      <Radio className="radio" value={MappingType.WidgetLevel} data-test="WidgetParameterOption">
        Widget parameter
      </Radio>
      <Radio className="radio" value={MappingType.StaticValue} data-test="StaticValueOption">
        Static value
      </Radio>
    </Radio.Group>
  );

  const renderDashboardAddNew = () => (
    <Input
      value={localMapping.mapTo}
      aria-label="Parameter name (key)"
      onChange={e => updateParamMapping({ mapTo: e.target.value })}
    />
  );

  const renderDashboardMapToExisting = () => {
    const options = map(existingParamNames, paramName => ({ label: paramName, value: paramName }));

    return <Select value={localMapping.mapTo} onChange={mapTo => updateParamMapping({ mapTo })} options={options} />;
  };

  const renderStaticValue = () => (
    <ParameterValueInput
      type={localMapping.param.type}
      value={localMapping.param.normalizedValue}
      enumOptions={localMapping.param.enumOptions}
      queryId={localMapping.param.queryId}
      parameter={localMapping.param}
      onSelect={value => updateParamMapping({ value })}
    />
  );

  const renderInputBlock = () => {
    switch (localMapping.type) {
      case MappingType.DashboardAddNew:
        return ["Key", "Enter a new parameter keyword", renderDashboardAddNew()];
      case MappingType.DashboardMapToExisting:
        return ["Key", "Select from a list of existing parameters", renderDashboardMapToExisting()];
      case MappingType.StaticValue:
        return ["Value", null, renderStaticValue()];
      default:
        return [];
    }
  };

  const [label, help, input] = renderInputBlock();

  return (
    <Form layout="horizontal" className={isDarkMode ? "dark-mode" : ""}>
      <Form.Item label="Source" {...formItemProps}>
        {renderMappingTypeSelector()}
      </Form.Item>
      <Form.Item
        style={{ height: 60, visibility: input ? "visible" : "hidden" }}
        label={label}
        {...formItemProps}
        validateStatus={inputError ? "error" : ""}
        help={inputError || help}
      >
        {input}
      </Form.Item>
    </Form>
  );
}

ParameterMappingInput.propTypes = {
  mapping: PropTypes.object,
  existingParamNames: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  inputError: PropTypes.string,
};

ParameterMappingInput.defaultProps = {
  mapping: {},
  existingParamNames: [],
  onChange: () => {},
  inputError: null,
};

export function ParameterMappingListInput({ mappings, existingParams, onChange }) {
  const { isDarkMode } = useTheme();
  const updateParamMapping = useCallback((oldMapping, newMapping) => {
    const updatedMappings = [...mappings];
    const index = findIndex(updatedMappings, oldMapping);
    if (index >= 0) {
      updatedMappings[index] = newMapping;
    } else {
      updatedMappings.push(newMapping);
    }
    onChange(updatedMappings);
  }, [mappings, onChange]);

  const getStringValue = useCallback((value) => {
    if (!value) {
      return "";
    }

    if (value instanceof Object && "start" in value && "end" in value) {
      return `${value.start} ~ ${value.end}`;
    }

    if (typeof value === "object") {
      return map(value, v => getStringValue(v)).join(", ");
    }

    return value.toString();
  }, []);

  const getDefaultValue = React.useCallback((mapping, existingParams) => {
    const { type, mapTo, name } = mapping;
    let { param } = mapping;

    if (type === MappingType.DashboardMapToExisting && mapTo !== name) {
      const mappedTo = find(existingParams, { name: mapTo });
      if (mappedTo) {
        param = mappedTo;
      }
    } else if (type === MappingType.StaticValue) {
      param = cloneParameter(param).setValue(mapping.value);
    }

    let value = Parameter.getExecutionValue(param);

    if (param.hasDynamicValue) {
      value = param.normalizedValue.name;
    }

    return getStringValue(value);
  }, [getStringValue]);

  const getSourceTypeLabel = React.useCallback(({ type, mapTo }) => {
    switch (type) {
      case MappingType.DashboardAddNew:
      case MappingType.DashboardMapToExisting:
        return (
          <Fragment>
            Dashboard <Tag className="tag">{mapTo}</Tag>
          </Fragment>
        );
      case MappingType.WidgetLevel:
        return "Widget parameter";
      case MappingType.StaticValue:
        return "Static value";
      default:
        return "";
    }
  }, []);

  const dataSource = React.useMemo(() => mappings.map(mapping => ({ mapping })), [mappings]);

  return (
    <div className={`parameters-mapping-list ${isDarkMode ? "dark-mode" : ""}`}>
      <Table
        dataSource={dataSource}
        size="middle"
        pagination={false}
        rowKey={(record, idx) => `row${idx}`}
        className={isDarkMode ? "ant-table-dark" : ""}
      >
        <Table.Column
          title="Title"
          dataIndex="mapping"
          key="title"
          render={mapping => (
            <TitleEditor
              existingParams={existingParams}
              mapping={mapping}
              onChange={newMapping => updateParamMapping(mapping, newMapping)}
            />
          )}
        />
        <Table.Column
          title="Keyword"
          dataIndex="mapping"
          key="keyword"
          className="keyword"
          render={mapping => <code>{`{{ ${mapping.name} }}`}</code>}
        />
        <Table.Column
          title="Default Value"
          dataIndex="mapping"
          key="value"
          render={mapping => getDefaultValue(mapping, existingParams)}
        />
        <Table.Column
          title="Value Source"
          dataIndex="mapping"
          key="source"
          render={mapping => {
            const existingParamsNames = existingParams
              .filter(({ type }) => type === mapping.param.type) // exclude mismatching param types
              .map(({ name }) => name); // keep names only

            return (
              <Fragment>
                {getSourceTypeLabel(mapping)}{" "}
                <MappingEditor
                  mapping={mapping}
                  existingParamNames={existingParamsNames}
                  onChange={(oldMapping, newMapping) => updateParamMapping(oldMapping, newMapping)}
                />
              </Fragment>
            );
          }}
        />
      </Table>
    </div>
  );
}

ParameterMappingListInput.propTypes = {
  mappings: PropTypes.arrayOf(PropTypes.object),
  existingParams: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func,
};

ParameterMappingListInput.defaultProps = {
  mappings: [],
  existingParams: [],
  onChange: () => {},
};


function MappingEditor({ mapping: initialMapping, existingParamNames, onChange }) {
  const [visible, setVisible] = useState(false);
  const [mapping, setMapping] = useState(clone(initialMapping));
  const [inputError, setInputError] = useState(null);
  const { isDarkMode } = useTheme();

  const onVisibleChange = useCallback((isVisible) => {
    if (isVisible) {
      show();
    } else {
      hide();
    }
  }, [show, hide]);

  const onMappingChange = useCallback((newMapping) => {
    let error = null;

    if (newMapping.type === MappingType.DashboardAddNew) {
      if (isEmpty(newMapping.mapTo)) {
        error = "Keyword must have a value";
      } else if (includes(existingParamNames, newMapping.mapTo)) {
        error = "A parameter with this name already exists";
      }
    }

    setMapping(newMapping);
    setInputError(error);
  }, [existingParamNames]);

  const save = useCallback(() => {
    onChange(initialMapping, mapping);
    hide();
  }, [initialMapping, mapping, onChange, hide]);

  const show = useCallback(() => {
    setVisible(true);
    setMapping(clone(initialMapping)); // restore original state
  }, [initialMapping]);

  const hide = useCallback(() => {
    setVisible(false);
  }, []);

  const renderContent = () => (
    <div className={`parameter-mapping-editor ${isDarkMode ? "dark-mode" : ""}`} data-test="EditParamMappingPopover">
      <header>
        Edit Source and Value <HelpTrigger type="VALUE_SOURCE_OPTIONS" />
      </header>
      <ParameterMappingInput
        mapping={mapping}
        existingParamNames={existingParamNames}
        onChange={onMappingChange}
        inputError={inputError}
      />
      <footer>
        <Button onClick={hide}>Cancel</Button>
        <Button onClick={save} disabled={!!inputError} type="primary">
          OK
        </Button>
      </footer>
    </div>
  );

  return (
    <Popover
      placement="left"
      trigger="click"
      content={renderContent()}
      visible={visible}
      onVisibleChange={onVisibleChange}>
      <Button size="small" type="dashed" data-test={`EditParamMappingButton-${mapping.param.name}`}>
        <EditOutlinedIcon />
      </Button>
    </Popover>
  );
}

MappingEditor.propTypes = {
  mapping: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  existingParamNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};